/**
 * Singleton Google Maps API loader.
 *
 * Uses the @googlemaps/js-api-loader v2 functional API (setOptions +
 * importLibrary) which injects the script with `loading=async`, suppressing
 * the "loaded directly without loading=async" console warning produced by the
 * old react-google-autocomplete script injection.
 *
 * NOTE: We continue to instantiate google.maps.places.Autocomplete (the old
 * class API) because the replacement — PlaceAutocompleteElement — is a Web
 * Component that renders its own shadow-DOM input and cannot be attached to
 * existing styled <input> elements without a full UI redesign. Google has
 * committed to 12+ months notice before removing Autocomplete, so this is the
 * pragmatic middle ground: async loading, no loading warning, one clear TODO.
 *
 * TODO: migrate to PlaceAutocompleteElement once a suitable adapter pattern
 * is established (https://developers.google.com/maps/documentation/javascript/places-migration-overview)
 */

import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

const API_KEY = (import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY ?? '';

// Configure the loader once — must happen before any importLibrary call.
setOptions({ key: API_KEY, v: 'weekly' });

let _promise: Promise<void> | null = null;

export function loadGoogleMaps(): Promise<void> {
  if (_promise) return _promise;

  // If google.maps.places is somehow already available (e.g. preloaded), skip.
  if (typeof window !== 'undefined' && window.google?.maps?.places) {
    _promise = Promise.resolve();
    return _promise;
  }

  // Load places (for Autocomplete / PlacesService) and routes (for
  // DirectionsService / DirectionsRenderer) in parallel.
  _promise = Promise.all([
    importLibrary('places'),
    importLibrary('routes'),
  ]).then(() => undefined).catch(error => {
    _promise = null;
    throw error;
  });
  return _promise;
}
